@if (option(); as option) {
  @if (!hideDetails()) {
    <section class="m-t-24 md:m-0 text-md">
      <header>
        <div
          class="flex flex-column md:flex-row md:flex-align-center flex-justify-between gap-24"
        >
          <h3 class="heading-sm m-b-12 md:m-b-0">
            {{ option.cardDetails.title }}
          </h3>
          @if (planOptionsPrices().length > 1 && !disablePeriodicityToggle) {
            <yo-segmented-control
              [control]="form.controls.periodicity"
              [options]="planOptionsPrices()"
            >
              <ng-template #optionTemplateRef let-option="option">
                {{ option.label | parsePeriodicity }}
                <span *ngIf="option.discount" class="discount-label">
                  {{ option.discount }}
                </span>
              </ng-template>
            </yo-segmented-control>
          }
        </div>
        <hr class="m-t-12 m-b-20" />
      </header>

      @if (option.type === 'PLAN') {
        <ng-container *ngTemplateOutlet="planInfo"></ng-container>
      } @else {
        <ng-container *ngTemplateOutlet="ticketInfo"></ng-container>
      }

      <div
        class="flex md:none flex-justify-between bg-transparent m-t-24 p-20 p-x-16 rounded-sm"
      >
        <strong i18n="@@paymentTotalDueLabel">To be paid</strong>
        <div class="text-right">
          <ng-container
            *ngTemplateOutlet="
              totalDueTpl;
              context: {
                description: selectedPlanPrice()?.description,
              }
            "
          ></ng-container>
        </div>
      </div>
    </section>
  }
  @if (additionalPaymentConfig(); as paymentConfig) {
    <joymo-payment-handler
      [additionalConfig]="paymentConfig"
      [channelId]="channel()?.docId"
      [videoId]="videoId()"
      [option]="option"
      (onTaxCalulcationAvailable)="
        handleTaxCalculation($event.type, $event.calculation)
      "
      (onActionResult)="onActionResult.emit($event)"
    >
    </joymo-payment-handler>
  }
}

<ng-template #ticketInfo>
  @if (ticket(); as ticket) {
    <ng-container
      *ngTemplateOutlet="
        genericInfoTpl;
        context: {
          longDesc: ticket.cardDetails.description,
          price: ticket.optionPrice?.price,
          title: ticket.cardDetails.title,
        }
      "
    ></ng-container>
    @if (!isTicketRedemptionLimited()) {
      <p class="text-md">
        {{ ticket.cardDetails.validityOrExpiryInfo }}
      </p>
    } @else {
      <div class="flex flex-column gap-2 m-t-8 md:w-2/3 text-md">
        <strong>{{ ticket.cardDetails.validityOrExpiryInfo }}</strong>
        <p>The counting starts from the first watching.</p>
        <p>You can safely buy the ticket now and watch the stream later.</p>
      </div>
    }
  }
</ng-template>

<ng-template #planInfo>
  @if (plan(); as plan) {
    @if (activePeriodicity(); as activePeriodicity) {
      <joymo-offer-installments
        *ngIf="
          activePeriodicity.installments && !form.value.coupon && currency()
        "
        (onToggle)="form.controls.useInstallments.setValue($event)"
        [currency]="currency()"
        [billingPeriodConfig]="activePeriodicity"
        [hidden]="disableInstallments"
        class="block m-b-20"
      ></joymo-offer-installments>

      <ng-container
        *ngTemplateOutlet="
          genericInfoTpl;
          context: {
            longDesc: plan.planData.descriptionLong,
            periodicity: selectedPlanPrice()?.periodicity,
            price: selectedPlanPrice()?.price,
            description: selectedPlanPrice()?.description,
            title: plan.cardDetails.title,
            trialLabel: selectedPlanPrice()?.trialPeriod,
            showInstallmentsDisclaimer: form.value.useInstallments,
          }
        "
      ></ng-container>

      <joymo-ticket-coupon
        *ngIf="
          activePeriodicity.allowPromoCodes &&
          !disableCoupons &&
          !form.value.useInstallments
        "
        (onDiscountedPrice)="handleDiscountedPrice($event)"
        [control]="form.controls.coupon"
        [periodicity]="form.value.periodicity"
        [plan]="plan.planData"
        class="block m-t-20"
      ></joymo-ticket-coupon>
    }
  }
</ng-template>

<ng-template
  #genericInfoTpl
  let-longDesc="longDesc"
  let-periodicity="periodicity"
  let-price="price"
  let-trialLabel="trialLabel"
  let-description="description"
  let-firstPayment="firstPayment"
  let-showInstallmentsDisclaimer="showInstallmentsDisclaimer"
>
  <div class="flex flex-column md:flex-row flex-justify-between gap-24">
    <div data-restricted-width>
      <p>{{ longDesc }}</p>
      <p [hidden]="!trialLabel || hideTrialLabel" class="m-t-8">
        <strong class="block text-semibold">{{ trialLabel }}</strong>
        <span [hidden]="!firstPayment">{{ firstPayment }}</span>
      </p>
      <p [hidden]="!showInstallmentsDisclaimer" class="m-t-8">
        <strong class="block">{{ billingLabels.installmentsHeading }}</strong>
        <span>{{ billingLabels.installmentsDisclaimer }}</span>
      </p>
    </div>

    <div class="none md:block text-right">
      <ng-container
        *ngTemplateOutlet="
          totalDueTpl;
          context: {
            description: description,
          }
        "
      ></ng-container>
    </div>
  </div>
</ng-template>

<ng-template #taxLines let-prefix="prefix" let-lines="lines">
  @if (lines.length === 1) {
    <span
      >{{ prefix }}&nbsp;({{ lines[0].name }},&nbsp;{{ lines[0].rate }})</span
    >
  } @else if (lines.length > 1) {
    <span>{{ prefix }}</span>
    @for (i of lines; track $index) {
      <span>{{ i.name }},&nbsp;{{ i.rate }}</span>
    }
  }
</ng-template>

<ng-template #totalDueTpl let-description="description">
  @if (toBePaid(); as toBePaid) {
    <strong class="text-lg">{{ toBePaid.total | price: currency() }}</strong>
    @if (toBePaid.taxLines; as lines) {
      <div class="flex flex-column gap-2">
        <ng-container
          *ngTemplateOutlet="
            taxLines;
            context: { prefix: 'Tax included', lines: lines.inclusive }
          "
        ></ng-container>
        <ng-container
          *ngTemplateOutlet="
            taxLines;
            context: { prefix: 'Tax applies', lines: lines.exclusive }
          "
        ></ng-container>
      </div>
    }
    @if (activePeriodicity()) {
      <div class="flex flex-column gap-2">
        <p [hidden]="form.value.useInstallments" class="text-sm">
          {{ description }}
        </p>

        @if (activePeriodicity()?.installments; as installments) {
          <div
            [hidden]="!form.value.useInstallments"
            class="flex flex-column text-sm"
          >
            <span>
              {{ billingLabels.periodicity[installments.period] }}
            </span>
            @if (totalInstallmentPrice()) {
              <span>
                {{ totalInstallmentPrice() | price: currency() }} in total
              </span>
            }
          </div>
        }
      </div>
    }
  }
</ng-template>
